import { SignInMethod } from '@frontend/auth-helpers';
import { metricsLogger } from '@frontend/tracking';
import type { AuthAttemptsBucket } from './auth-flow-timer';

/**
 * `existing_session` is the auto-accept path: the user already has a valid session, so no credentials
 * are entered. It's tracked so accept-flow failures on that path are visible, but filter it out when
 * looking at credential success rates.
 */
export type AuthMethod = SignInMethod | 'password' | 'webauthn' | 'existing_session';
/** `cancelled` is a user-initiated abort (e.g. dismissing the passkey prompt) and shouldn't count against the success rate. */
export type AuthOutcome = 'success' | 'fail' | 'cancelled';

const TEAM = 'users-infra';
const CATEGORY = 'auth';

/**
 * Durations longer than this are dropped rather than reported. A genuine auth flow (including IdP login
 * and MFA) doesn't take this long; anything beyond it is a stale start time (orphaned storage entry, a
 * tab left open overnight, laptop sleep, clock change) and would wreck the p90+.
 */
export const MAX_TIME_TO_AUTH_MS = 10 * 60 * 1000;

interface RecordAuthResultArgs {
  method: AuthMethod;
  outcome: AuthOutcome;
  failureReason?: string;
}

/** Counter for auth attempts. Slice by `method` for the method distribution and by `outcome` for success/fail rate. */
export function recordAuthResult({ method, outcome, failureReason }: RecordAuthResultArgs): void {
  try {
    metricsLogger.metric('auth.result', 1, {
      attributes: {
        team: TEAM,
        category: CATEGORY,
        method,
        outcome,
        ...(failureReason ? { failure_reason: failureReason } : {}),
      },
    });
  } catch (err) {
    console.error('Failed to record auth.result metric', err);
  }
}

interface RecordTimeToAuthArgs {
  durationMs: number | undefined;
  method: AuthMethod;
  outcome: AuthOutcome;
  /** whether the flow included an MFA code step, which adds human input time */
  mfa?: boolean;
  /** how many credential attempts it took (see AuthFlowTimer) */
  attempts?: AuthAttemptsBucket;
  /** the tab was hidden at some point during the flow */
  backgrounded?: boolean;
  /** the user went idle at some point; the idle time itself is excluded from the duration */
  idle?: boolean;
}

/**
 * Distribution of end-to-end auth duration in ms, sliced by `method` and `outcome`. Missing, negative, or
 * implausibly long durations (see `MAX_TIME_TO_AUTH_MS`) are dropped.
 */
export function recordTimeToAuth({
  durationMs,
  method,
  outcome,
  mfa,
  attempts,
  backgrounded,
  idle,
}: RecordTimeToAuthArgs): void {
  if (!isPlausibleDuration(durationMs)) {
    return;
  }
  try {
    metricsLogger.timing('auth.time_to_auth', Math.round(durationMs), {
      attributes: {
        team: TEAM,
        category: CATEGORY,
        method,
        outcome,
        ...(mfa !== undefined ? { mfa } : {}),
        ...(attempts !== undefined ? { attempts } : {}),
        ...(backgrounded !== undefined ? { backgrounded } : {}),
        ...(idle !== undefined ? { idle } : {}),
      },
    });
  } catch (err) {
    console.error('Failed to record auth.time_to_auth metric', err);
  }
}

function isPlausibleDuration(durationMs: number | undefined): durationMs is number {
  return (
    durationMs !== undefined && Number.isFinite(durationMs) && durationMs >= 0 && durationMs <= MAX_TIME_TO_AUTH_MS
  );
}
